import React from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface Portfolio {
  id: string;
  name: string;
  created_at: string;
  portfolio_type?: string;
}

interface PortfolioSelectorProps {
  portfolios: Portfolio[];
  selectedPortfolioId: string | null;
  setSelectedPortfolioId: (id: string) => void;
  onCreateNew: () => void;
  isLoading: boolean;
  isComboOpen: boolean;
  setIsComboOpen: (isOpen: boolean) => void;
}

export default function PortfolioSelector({
  portfolios,
  selectedPortfolioId,
  setSelectedPortfolioId,
  onCreateNew,
  isLoading,
  isComboOpen,
  setIsComboOpen,
}: PortfolioSelectorProps) {
  return (
    <div className="grid gap-2">
      <Label htmlFor="portfolio">Select Portfolio</Label>
      <div className="text-xs text-muted-foreground">To add holdings, choose a Personal or Client portfolio.</div>
      <Popover open={isComboOpen} onOpenChange={setIsComboOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={isComboOpen}
            className="w-full justify-between"
            disabled={isLoading}
          >
            {selectedPortfolioId
              ? portfolios.find((portfolio) => portfolio.id === selectedPortfolioId)?.name
              : "Select a portfolio..."}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0">
          <Command>
            <CommandInput placeholder="Search portfolios..." />
            <CommandEmpty>No portfolios found.</CommandEmpty>
            <CommandList>
              {(() => {
                const eligible = (portfolios || []).filter(p => (p as any)?.portfolio_type !== 'model');
                const models = (portfolios || []).filter(p => (p as any)?.portfolio_type === 'model');
                return (
                  <>
                    <CommandGroup>
                      {eligible.map((portfolio) => (
                        <CommandItem
                          key={portfolio.id}
                          value={portfolio.id}
                          onSelect={() => {
                            setSelectedPortfolioId(portfolio.id);
                            setIsComboOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedPortfolioId === portfolio.id
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                          {portfolio.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    {models.length > 0 && (
                      <>
                        <div className="px-2 pt-2 pb-1 text-xs text-muted-foreground">Not eligible</div>
                        <CommandGroup>
                          {models.map((portfolio) => (
                            <CommandItem
                              key={portfolio.id}
                              value={portfolio.id}
                              // Prevent selection for model portfolios
                              onSelect={() => { /* disabled */ }}
                              className="opacity-60 pointer-events-none"
                            >
                              <Check className="mr-2 h-4 w-4 opacity-0" />
                              {portfolio.name}
                              <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide">Model</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </>
                    )}
                  </>
                );
              })()}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      
      <Button 
        variant="link" 
        className="mt-2 justify-start px-0"
        onClick={onCreateNew}
      >
        <Plus className="mr-2 h-4 w-4" />
        Create new portfolio
      </Button>
    </div>
  );
}
